import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { pushModal, replaceModal } from 'loot-core/src/client/actions';
import { send } from 'loot-core/src/platform/client/fetch';

import {
  REACT_APP_BILLING_STATUS,
  REACT_APP_COACH,
} from '../../coaches/coachVariables';
import { theme } from '../../style';
import { Button } from '../common/Button2';
import { Text } from '../common/Text';
import { View } from '../common/View';
import { MOBILE_NAV_HEIGHT } from '../mobile/MobileNavTabs';
import { Page } from '../Page';
import { LoadingIndicator } from '../reports/LoadingIndicator';

import {
  type BillingSummary,
  PREMIUM_COACHES,
  accessEnds,
  errorText,
  formatDate,
  formatMoney,
  isInApp,
  planLabel,
} from './billing';

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <View
      style={{
        backgroundColor: theme.tableBackground,
        border: `1px solid ${theme.tableBorder}`,
        borderRadius: 8,
        padding: 16,
        gap: 8,
      }}
    >
      {title && (
        <Text style={{ fontWeight: 600, fontSize: 15, color: theme.pageText }}>
          {title}
        </Text>
      )}
      {children}
    </View>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'error' | 'warning' | 'notice';
  children: ReactNode;
}) {
  const colors = {
    error: [theme.errorBackground, theme.errorTextDarker, theme.errorBorder],
    warning: [theme.warningBackground, theme.warningTextDark, theme.warningBorder],
    notice: [theme.noticeBackgroundLight, theme.noticeTextDark, theme.noticeBorder],
  }[tone];
  return (
    <View
      role={tone === 'error' ? 'alert' : 'status'}
      style={{
        backgroundColor: colors[0],
        color: colors[1],
        border: `1px solid ${colors[2]}`,
        borderRadius: 8,
        padding: 14,
        gap: 10,
        lineHeight: 1.5,
      }}
    >
      {children}
    </View>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <Text style={{ color: theme.pageTextSubdued }}>{label}</Text>
      <Text style={{ color: theme.pageText, textAlign: 'right' }}>{children}</Text>
    </View>
  );
}

export function BillingPage() {
  const dispatch = useDispatch();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmingPlan, setConfirmingPlan] = useState<'basic' | 'premium' | null>(
    null,
  );

  const url = String(window.location.href);

  const load = useCallback(async () => {
    const result = await send('billing-summary', { url });
    if (result.error) {
      setLoadError(errorText(result.error));
    } else {
      setLoadError(null);
      setSummary(result.data as BillingSummary);
    }
  }, [url]);

  useEffect(() => {
    load();
  }, [load]);

  async function openPortal(flow?: 'payment_method_update') {
    setBusy(flow || 'portal');
    setActionError(null);
    const result = await send('billing-portal', { url, flow, inApp: isInApp() });
    if (result.error || !result.data) {
      setBusy(null);
      setActionError(errorText(result.error));
      return;
    }
    window.location.href = result.data;
  }

  async function resume() {
    setBusy('resume');
    setActionError(null);
    const result = await send('billing-resume', { url, inApp: isInApp() });
    setBusy(null);
    if (result.error) {
      setActionError(errorText(result.error));
      return;
    }
    await load();
  }

  async function switchPlan(plan: 'basic' | 'premium') {
    setBusy('switch');
    setActionError(null);
    const result = await send('billing-switch-plan', {
      url,
      plan,
      fromCancel: false,
      inApp: isInApp(),
    });
    setBusy(null);
    setConfirmingPlan(null);
    if (result.error) {
      setActionError(errorText(result.error));
      return;
    }
    await load();
  }

  function startCancel() {
    dispatch(
      pushModal('cancel-subscription', {
        summary,
        onDone: () => load(),
      }),
    );
  }

  function subscribe() {
    dispatch(replaceModal('subscribe', {}));
  }

  let content: ReactNode;

  if (loadError) {
    content = (
      <Banner tone="error">
        <Text>{loadError}</Text>
        <View style={{ flexDirection: 'row' }}>
          <Button onPress={() => load()}>Try again</Button>
        </View>
      </Banner>
    );
  } else if (!summary) {
    content = <LoadingIndicator />;
  } else if (summary.source === 'none' || !summary.isCurrent) {
    content = (
      <NoWebSubscription
        summary={summary}
        onSubscribe={subscribe}
        onCancelStore={startCancel}
      />
    );
  } else {
    const ends = accessEnds(summary);
    const coachOffersPremium = PREMIUM_COACHES.includes(REACT_APP_COACH);
    const otherPlan: 'basic' | 'premium' | null =
      summary.plan === 'premium'
        ? 'basic'
        : summary.plan === 'basic' && coachOffersPremium
          ? 'premium'
          : null;

    content = (
      <>
        {summary.pastDue && (
          <Banner tone="error">
            <Text style={{ fontWeight: 600 }}>
              Your last payment didn't go through.
            </Text>
            <Text>
              Update your card so you don't lose access to your budget. We'll
              try the payment again once it's updated.
            </Text>
            <View style={{ flexDirection: 'row' }}>
              <Button
                variant="primary"
                isDisabled={busy !== null}
                onPress={() => openPortal('payment_method_update')}
              >
                Update card
              </Button>
            </View>
          </Banner>
        )}

        {summary.cancelAtPeriodEnd && (
          <Banner tone="warning">
            <Text>
              Your subscription ends on <strong>{formatDate(ends)}</strong>. You
              keep full access until then.
            </Text>
            <View style={{ flexDirection: 'row' }}>
              <Button
                variant="primary"
                isDisabled={busy !== null}
                onPress={resume}
              >
                {busy === 'resume' ? 'Saving…' : 'Keep my subscription'}
              </Button>
            </View>
          </Banner>
        )}

        {actionError && (
          <Banner tone="error">
            <Text>{actionError}</Text>
          </Banner>
        )}

        <Card title="Your plan">
          <Row label="Plan">{planLabel(summary)}</Row>
          <Row label="Price">
            {formatMoney(summary.amount, summary.currency)} a {summary.interval}
          </Row>
          {summary.status === 'trialing' && !summary.cancelAtPeriodEnd && (
            <Row label="Free trial ends">{formatDate(summary.trialEnd)}</Row>
          )}
          {!summary.cancelAtPeriodEnd && (
            <Row label="Next payment">
              {formatMoney(summary.amount, summary.currency)} on{' '}
              {formatDate(summary.currentPeriodEnd)}
            </Row>
          )}
        </Card>

        {otherPlan && !summary.cancelAtPeriodEnd && (
          <Card title={otherPlan === 'premium' ? 'Upgrade to Premium' : 'Switch to Basic'}>
            <Text style={{ color: theme.pageText, lineHeight: 1.5 }}>
              {otherPlan === 'premium'
                ? `${formatMoney(summary.prices.premium, summary.currency)} a month, including a one-hour session with your coach every month.`
                : `${formatMoney(summary.prices.basic, summary.currency)} a month. You keep your budget, bank connections and messages with your coach, without the monthly session.`}
            </Text>
            {confirmingPlan === otherPlan ? (
              <View style={{ gap: 8 }}>
                <Text style={{ color: theme.pageTextSubdued, lineHeight: 1.5 }}>
                  {otherPlan === 'premium'
                    ? summary.status === 'trialing'
                      ? `Premium starts now. Your first payment of ${formatMoney(summary.prices.premium, summary.currency)} is when your trial ends.`
                      : `You'll pay the difference for the rest of this month today, then ${formatMoney(summary.prices.premium, summary.currency)} a month.`
                    : `Basic starts now, and your next bill will be ${formatMoney(summary.prices.basic, summary.currency)}.`}
                </Text>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                  <Button
                    isDisabled={busy !== null}
                    onPress={() => setConfirmingPlan(null)}
                  >
                    Not now
                  </Button>
                  <Button
                    variant="primary"
                    isDisabled={busy !== null}
                    onPress={() => switchPlan(otherPlan)}
                  >
                    {busy === 'switch'
                      ? 'Saving…'
                      : otherPlan === 'premium'
                        ? 'Confirm upgrade'
                        : 'Confirm switch'}
                  </Button>
                </View>
              </View>
            ) : (
              <View style={{ flexDirection: 'row' }}>
                <Button
                  isDisabled={busy !== null}
                  onPress={() => setConfirmingPlan(otherPlan)}
                >
                  {otherPlan === 'premium' ? 'Upgrade to Premium' : 'Switch to Basic'}
                </Button>
              </View>
            )}
          </Card>
        )}

        <Card title="Payment method">
          <Text style={{ color: theme.pageText }}>
            {summary.paymentMethod
              ? `${summary.paymentMethod.label}${summary.paymentMethod.expires ? `, expires ${summary.paymentMethod.expires}` : ''}`
              : 'No payment method on file'}
          </Text>
          <View style={{ flexDirection: 'row' }}>
            <Button
              isDisabled={busy !== null}
              onPress={() => openPortal('payment_method_update')}
            >
              {busy === 'payment_method_update' ? 'Opening…' : 'Update card'}
            </Button>
          </View>
        </Card>

        {summary.invoices && summary.invoices.length > 0 && (
          <Card title="Past payments">
            {summary.invoices.map(invoice => (
              <View
                key={invoice.id}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: 12,
                  paddingTop: 6,
                  borderTop: `1px solid ${theme.tableBorderSeparator}`,
                }}
              >
                <Text style={{ color: theme.pageText }}>{formatDate(invoice.date)}</Text>
                <Text style={{ color: theme.pageText }}>
                  {formatMoney(invoice.amount, summary.currency)}
                  {invoice.status === 'paid' ? '' : ` (${invoice.status})`}
                  {invoice.url && (
                    <>
                      {' · '}
                      <a
                        href={invoice.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.pageTextLink }}
                      >
                        Receipt
                      </a>
                    </>
                  )}
                </Text>
              </View>
            ))}
          </Card>
        )}

        {!summary.cancelAtPeriodEnd && (
          <View style={{ alignItems: 'flex-start', marginTop: 4 }}>
            <Button variant="bare" isDisabled={busy !== null} onPress={startCancel}>
              <Text style={{ color: theme.errorText }}>Cancel subscription</Text>
            </Button>
          </View>
        )}
      </>
    );
  }

  return (
    <Page header="Billing" style={{ paddingBottom: MOBILE_NAV_HEIGHT }}>
      <View
        style={{
          marginTop: 10,
          marginBottom: 20,
          maxWidth: 560,
          gap: 16,
        }}
      >
        {content}
      </View>
    </Page>
  );
}

function NoWebSubscription({
  summary,
  onSubscribe,
  onCancelStore,
}: {
  summary: BillingSummary;
  onSubscribe: () => void;
  onCancelStore: () => void;
}) {
  const isPaid = REACT_APP_BILLING_STATUS === 'paid';
  const isSponsored = REACT_APP_BILLING_STATUS === 'sponsored';

  if (isSponsored) {
    return (
      <Card title="Your plan">
        <Text style={{ color: theme.pageText }}>
          Your account is sponsored, so there's nothing to pay.
        </Text>
      </Card>
    );
  }

  if (isPaid && summary.source === 'none') {
    // Paid, but not through the website: almost always the iPhone app.
    return (
      <Card title="Your plan">
        <Text style={{ color: theme.pageText, lineHeight: 1.5 }}>
          Your subscription is through Apple, so Apple handles your payments.
        </Text>
        <Text style={{ color: theme.pageTextSubdued, lineHeight: 1.5 }}>
          {isInApp()
            ? 'You can change or cancel it in the App Store.'
            : 'To change or cancel it, open Settings on your iPhone, tap your name, then Subscriptions. Something not right? Email admin@mybudgetcoach.com.'}
        </Text>
        <View style={{ flexDirection: 'row' }}>
          <Button onPress={onCancelStore}>Cancel subscription</Button>
        </View>
      </Card>
    );
  }

  return (
    <Card title="Your plan">
      <Text style={{ color: theme.pageText, lineHeight: 1.5 }}>
        {summary.endedAt
          ? `Your subscription ended on ${formatDate(summary.endedAt)}.`
          : "You're not subscribed yet."}{' '}
        Subscribe to keep your budget and your coach.
      </Text>
      <View style={{ flexDirection: 'row' }}>
        <Button variant="primary" onPress={onSubscribe}>
          {summary.endedAt ? 'Subscribe again' : 'Subscribe'}
        </Button>
      </View>
    </Card>
  );
}
